import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildDocumentKey,
  getSpecialityEvidenceDocuments,
  getSupportingDocuments,
  prepareDocumentSelection,
} from "../utils/document-policy";

const GALLERY_SCOPES = {
  SUPPORTING: "supporting",
  SPECIALITY: "speciality",
};

const releasePreviews = (documents) => documents.forEach((document) => {
  if (document.previewUrl) URL.revokeObjectURL(document.previewUrl);
});

const useDocuments = ({ maxSupportingDocuments, isSubmitting, onResult }) => {
  const [documents, setDocuments] = useState([]);
  const documentsRef = useRef([]);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [galleryScope, setGalleryScope] = useState(GALLERY_SCOPES.SUPPORTING);
  const [gallerySpecialityId, setGallerySpecialityId] = useState(null);
  const [activeDocumentIndex, setActiveDocumentIndex] = useState(0);

  const specialityEvidenceDocuments = useMemo(
    () => getSpecialityEvidenceDocuments(documents),
    [documents],
  );
  const supportingDocuments = useMemo(
    () => getSupportingDocuments(documents),
    [documents],
  );
  const galleryDocuments =
    galleryScope === GALLERY_SCOPES.SPECIALITY
      ? specialityEvidenceDocuments.filter(
        (document) =>
          !gallerySpecialityId ||
          Number(document.specialityId) === Number(gallerySpecialityId),
      )
      : supportingDocuments;
  const activeDocument = galleryDocuments[activeDocumentIndex] || null;

  const updateDocuments = useCallback((next) => {
    documentsRef.current = next;
    setDocuments(next);
    if (!next.length) setIsDocumentsModalOpen(false);
  }, []);

  const resetDocuments = useCallback(() => {
    releasePreviews(documentsRef.current);
    updateDocuments([]);
  }, [updateDocuments]);

  useEffect(() => () => releasePreviews(documentsRef.current), []);

  useEffect(() => {
    if (!galleryDocuments.length) {
      setIsDocumentsModalOpen(false);
      setActiveDocumentIndex(0);
      return;
    }
    setActiveDocumentIndex((index) =>
      Math.min(index, galleryDocuments.length - 1),
    );
  }, [galleryDocuments.length]);

  const removeSpecialityEvidence = (specialityId = null) => {
    const removed = getSpecialityEvidenceDocuments(documentsRef.current).filter(
      (document) =>
        !specialityId || Number(document.specialityId) === Number(specialityId),
    );
    releasePreviews(removed);
    updateDocuments(
      documentsRef.current.filter((document) => !removed.includes(document)),
    );
  };

  const removeSpecialityEvidenceById = useCallback((specialityId) => {
    const removed = getSpecialityEvidenceDocuments(documentsRef.current).filter(
      (document) => Number(document.specialityId) === Number(specialityId),
    );
    releasePreviews(removed);
    updateDocuments(
      documentsRef.current.filter((document) => !removed.includes(document)),
    );
  }, [updateDocuments]);

  const onFilesChange = (event, evidence = {}) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (isSubmitting || !files.length) return;

    const selection = prepareDocumentSelection(documentsRef.current, files, {
      maxSupportingDocuments,
      requirementKey: evidence.requirementKey || "",
    });
    if (selection.error) {
      onResult({ type: "danger", message: selection.error });
      return;
    }

    const additions = selection.files.map((file) => ({
      id: `${buildDocumentKey(file)}${evidence.specialityId ? `-${evidence.specialityId}` : ""}`,
      name: evidence.requirementKey ? `Evidencia especialidad - ${file.name}` : file.name,
      requirementKey: evidence.requirementKey || "",
      specialityId: evidence.specialityId || null,
      size: file.size,
      type: file.type,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    updateDocuments([...documentsRef.current, ...additions]);
    onResult(selection.hasDuplicates
      ? { type: "warning", message: "Los archivos duplicados no se agregaron nuevamente." }
      : { type: "", message: "" });
  };

  const replaceRequirementDocument = (event, evidence) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (isSubmitting || !file || !evidence?.requirementKey) return;

    const matchesRequirement = (document) =>
      document.requirementKey === evidence.requirementKey &&
      Number(document.specialityId || 0) ===
        Number(evidence.specialityId || 0);
    const currentDocument = documentsRef.current.find(matchesRequirement);
    const remainingDocuments = documentsRef.current.filter(
      (document) => !matchesRequirement(document),
    );
    const selection = prepareDocumentSelection(remainingDocuments, [file], {
      maxSupportingDocuments,
      requirementKey: evidence.requirementKey,
    });

    if (selection.error) {
      onResult({ type: "danger", message: selection.error });
      return;
    }

    if (currentDocument) releasePreviews([currentDocument]);
    const document = {
      id: `${buildDocumentKey(file)}-${
        evidence.specialityId || evidence.requirementKey
      }`,
      name: file.name,
      requirementKey: evidence.requirementKey,
      specialityId: evidence.specialityId || null,
      size: file.size,
      type: file.type,
      file,
      previewUrl: URL.createObjectURL(file),
    };
    updateDocuments([...remainingDocuments, document]);
    onResult({ type: "", message: "" });
  };

  const removeDocument = (document) => {
    if (isSubmitting || !document?.id) return;
    const current = documentsRef.current;
    const target = current.find((item) => item.id === document.id);
    if (!target) return;
    releasePreviews([target]);
    updateDocuments(current.filter((item) => item.id !== document.id));
  };

  const openDocumentsModal = (document, scope) => {
    const currentDocuments =
      scope === GALLERY_SCOPES.SPECIALITY
        ? getSpecialityEvidenceDocuments(documentsRef.current).filter(
          (item) =>
            Number(item.specialityId) === Number(document?.specialityId),
        )
        : getSupportingDocuments(documentsRef.current);
    const selectedIndex = currentDocuments.findIndex(
      (item) => item.id === document?.id,
    );
    if (selectedIndex < 0) return;
    setGalleryScope(scope);
    setGallerySpecialityId(
      scope === GALLERY_SCOPES.SPECIALITY ? document.specialityId : null,
    );
    setActiveDocumentIndex(selectedIndex);
    setIsDocumentsModalOpen(true);
  };

  const moveDocument = (offset) => {
    if (galleryDocuments.length) {
      setActiveDocumentIndex((index) =>
        (index + offset + galleryDocuments.length) % galleryDocuments.length);
    }
  };

  return {
    documents,
    supportingDocuments,
    specialityEvidenceDocuments,
    galleryDocuments,
    activeDocument,
    activeDocumentIndex,
    isDocumentsModalOpen,
    onFilesChange,
    onRequirementFileChange: (event, requirementKey) =>
      replaceRequirementDocument(event, { requirementKey }),
    onSpecialityEvidenceFileChange: (event, specialityId) =>
      replaceRequirementDocument(event, {
        requirementKey: "specialtyDegreeMsp",
        specialityId: Number(specialityId),
      }),
    openSupportingDocumentsModal: (document) =>
      openDocumentsModal(document, GALLERY_SCOPES.SUPPORTING),
    openSpecialityEvidenceModal: (document) =>
      openDocumentsModal(document, GALLERY_SCOPES.SPECIALITY),
    closeDocumentsModal: () => setIsDocumentsModalOpen(false),
    goToPreviousDocument: () => moveDocument(-1),
    goToNextDocument: () => moveDocument(1),
    removeDocument,
    removeGalleryDocumentAt: (index) => removeDocument(galleryDocuments[index]),
    setActiveDocumentIndex,
    resetDocuments,
    removeSpecialityEvidence,
    removeSpecialityEvidenceById,
  };
};

export default useDocuments;
